import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cachedRequest, getCachedValue, invalidateCachedValue } from '../lib/requestCache';

export default function useStaleRequest({
  cacheKey,
  loader,
  ttlMs = 60_000,
  enabled = true,
  deps = [],
  initialValue = null,
}) {
  const depsKey = useMemo(() => JSON.stringify(deps ?? []), [deps]);
  const loaderRef = useRef(loader);

  useEffect(() => {
    loaderRef.current = loader;
  }, [loader]);

  const warmValue = useMemo(
    () => (enabled && cacheKey ? getCachedValue(cacheKey) : undefined),
    [enabled, cacheKey]
  );

  const [data, setData] = useState(() => (warmValue !== undefined ? warmValue : initialValue));
  const [loading, setLoading] = useState(() => enabled && warmValue === undefined);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const run = useCallback(async ({ forceRefresh = false } = {}) => {
    const currentLoader = loaderRef.current;
    if (!enabled || !cacheKey || typeof currentLoader !== 'function') {
      setLoading(false);
      return undefined;
    }

    const cached = getCachedValue(cacheKey);
    const hasCachedValue = cached !== undefined;

    if (hasCachedValue) {
      setData(cached);
      setLoading(false);
      setRefreshing(true);
    } else {
      setLoading(true);
      setRefreshing(false);
    }

    setError(null);

    try {
      const result = await cachedRequest(cacheKey, currentLoader, {
        ttlMs,
        forceRefresh: forceRefresh || hasCachedValue,
      });
      setData(result);
      return result;
    } catch (requestError) {
      setError(requestError);
      throw requestError;
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [cacheKey, enabled, ttlMs]);

  useEffect(() => {
    run().catch(() => {});
  }, [run, depsKey]);

  const revalidate = useCallback(() => run({ forceRefresh: true }), [run]);

  const invalidate = useCallback(() => {
    if (!cacheKey) return;
    invalidateCachedValue(cacheKey);
  }, [cacheKey]);

  return {
    data,
    loading,
    refreshing,
    error,
    setData,
    revalidate,
    invalidate,
    hasCachedValue: warmValue !== undefined,
  };
}
